<template>
  <div class="address-container">
    <!-- Adresse complète : on n'affiche plus que sa ligne, les champs se rouvrent avec « Modifier » -->
    <div v-if="isSummaryDisplayed" class="address-summary">
      <span class="address-summary-icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><path d="M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
      </span>
      <span class="address-summary-line" :title="summaryLine">{{ summaryLine }}</span>
      <button type="button" class="address-summary-edit" @click="isCollapsed = false">Modifier</button>
    </div>

    <div v-if="!isSummaryDisplayed" class="address-search">
      <div class="address-search-label">
        Rechercher une adresse
        <button v-if="collapsible && isComplete()" type="button" class="address-collapse" @click="isCollapsed = true">Réduire</button>
      </div>
      <div class="autocomplete-container">
        <CustomTextField :content=inputs.search />
        <div v-if="suggestions.length" class="suggestions-container">
          <div v-for="(suggestion, index) in suggestions" :key="index" class="suggestion" @click="selectSuggestion(suggestion)">
            {{ suggestion.label }}
          </div>
        </div>
      </div>
      <button v-if="!areFieldsDisplayed" type="button" class="address-manual" @click="isManualEntry = true">Saisir l’adresse à la main</button>
    </div>

    <div v-if="!isSummaryDisplayed && areFieldsDisplayed" class="address-fields">
      <div class="address-line">
        <div class="street-number">
          <CustomTextField :content=inputs.streetNumber />
        </div>
        <div class="street">
          <CustomTextField :content=inputs.street />
        </div>
      </div>

      <CustomTextField :content=inputs.addressLine2 />

      <div class="address-line">
        <div class="postal-code">
          <CustomTextField :content=inputs.postalCode />
        </div>
        <div class="city">
          <CustomTextField :content=inputs.city />
        </div>
      </div>

      <div class="address-line">
        <div class="country">
          <CustomTextField :content=inputs.country />
        </div>
        <div class="country-code">
          <CustomTextField :content=inputs.countryCode />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import {AddressSearchSession, fetchAddressSuggestions, getAddressSourceForClientType} from "./addressSearch";

const REQUIRED_ADDRESS_FIELDS = ['street', 'streetNumber', 'postalCode', 'city', 'country', 'countryCode'];

export default {
  name: 'CustomAddressField',
  components: {
    CustomTextField,
  },
  emits: ['update:value'],
  props: {
    content: {
      type: Object,
      required: true
    },
    // Type du client : détermine la source de recherche (entreprise française : source française, sinon internationale)
    clientType: {
      type: String,
      default: ''
    },
    // Une adresse complète se replie sur une seule ligne (les champs se rouvrent avec « Modifier »)
    collapsible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputs: {
        search: {label: '', value: ''},
        street: {label: 'Rue', value: '', required: true},
        streetNumber: {label: 'Numéro', value: '', required: true},
        addressLine2: {label: 'Complément d’adresse', value: ''},
        postalCode: {label: 'Code postal', value: '', required: true},
        city: {label: 'Ville', value: '', required: true},
        country: {label: 'Pays', value: '', required: true},
        countryCode: {label: 'Code pays', value: '', required: true}
      },
      address: {
        latitude: undefined,
        longitude: undefined
      },
      suggestions: [],
      searchTimeout: null,
      initializing: true,
      selectingSuggestion: false,
      skipNextSearch: false,
      lastQuery: '',
      isCollapsed: true,
      isManualEntry: false,
    }
  },
  computed: {
    addressSource() {
      return getAddressSourceForClientType(this.clientType);
    },
    isSummaryDisplayed() {
      return this.collapsible && this.isCollapsed && this.isComplete();
    },
    // Mode « repliable » : les champs détaillés n'apparaissent qu'une fois l'adresse choisie, partiellement saisie ou demandée à la main
    areFieldsDisplayed() {
      return !this.collapsible || this.isManualEntry || this.hasAddressValue();
    },
    summaryLine() {
      return [
        [this.inputs.streetNumber.value, this.inputs.street.value].filter(Boolean).join(' '),
        this.inputs.addressLine2.value,
        [this.inputs.postalCode.value, this.inputs.city.value].filter(Boolean).join(' '),
        this.inputs.country.value
      ].filter(Boolean).join(', ');
    }
  },
  created() {
    this.addressSearchSession = new AddressSearchSession();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout);
    this.addressSearchSession.invalidate();
  },
  mounted() {
    this.updateInputs();
    this.initializing = false;
  },
  watch: {
    content: {
      deep: true,
      handler() {
        this.updateInputs();
      }
    },
    clientType() {
      // Changement de type : on abandonne la recherche en cours et ses résultats, puis on relance la recherche sur la nouvelle source
      clearTimeout(this.searchTimeout);
      this.addressSearchSession.invalidate();
      this.suggestions = [];
      if (this.lastQuery) {
        this.searchAddress(this.lastQuery);
      }
    },
    'inputs.search.value'(value) {
      clearTimeout(this.searchTimeout);
      if (this.skipNextSearch) {
        this.skipNextSearch = false;
        return;
      }
      if (this.selectingSuggestion) {
        this.selectingSuggestion = false;
        return;
      }
      if (!value || value.length < 3) {
        this.lastQuery = '';
        this.addressSearchSession.invalidate();
        this.suggestions = [];
        return;
      }
      this.lastQuery = value;
      this.searchTimeout = setTimeout(() => this.searchAddress(value), 300);
    },
    inputs: {
      deep: true,
      handler() {
        this.emitValue();
      }
    }
  },
  methods: {
    updateInputs() {
      this.skipNextSearch = true;
      this.isCollapsed = true;
      this.isManualEntry = false;
      this.lastQuery = '';
      this.addressSearchSession.invalidate();
      this.suggestions = [];
      this.inputs.street.value = this.content.street || '';
      this.inputs.streetNumber.value = this.content.streetNumber || '';
      this.inputs.addressLine2.value = this.content.addressLine2 || '';
      this.inputs.postalCode.value = this.content.postalCode || '';
      this.inputs.city.value = this.content.city || '';
      this.inputs.country.value = this.content.country || '';
      this.inputs.countryCode.value = this.content.countryCode || '';
      this.address.latitude = this.content.latitude;
      this.address.longitude = this.content.longitude;
      this.inputs.search.value = this.content.street ? `${this.content.streetNumber ? this.content.streetNumber + ' ' : ''}${this.content.street}, ${this.content.postalCode || ''} ${this.content.city || ''}` : '';
    },
    async searchAddress(value) {
      const request = this.addressSearchSession.begin();
      try {
        const suggestions = await fetchAddressSuggestions(this.addressSource, value, request.signal);
        if (request.isCurrent()) {
          this.suggestions = suggestions;
        }
      } catch (error) {
        if (request.isCurrent()) {
          this.suggestions = [];
        }
      }
    },
    selectSuggestion(suggestion) {
      this.selectingSuggestion = true;
      this.lastQuery = '';
      this.addressSearchSession.invalidate();
      this.inputs.search.value = suggestion.label || '';
      this.inputs.street.value = suggestion.street || '';
      this.inputs.streetNumber.value = suggestion.streetNumber || this.extractStreetNumber(suggestion.label);
      this.inputs.postalCode.value = suggestion.postalCode || '';
      this.inputs.city.value = suggestion.city || '';
      this.inputs.country.value = suggestion.country || '';
      this.inputs.countryCode.value = suggestion.countryCode || '';
      this.address.latitude = suggestion.latitude;
      this.address.longitude = suggestion.longitude;
      this.suggestions = [];
      this.isCollapsed = true;
    },
    extractStreetNumber(fulltext) {
      const match = fulltext.match(/^\s*(\d+[A-Za-z]?(?:[-/]\d+[A-Za-z]?)?)/);
      return match ? match[1] : '';
    },
    getValue() {
      return {
        street: this.inputs.street.value,
        streetNumber: this.inputs.streetNumber.value,
        addressLine2: this.inputs.addressLine2.value,
        postalCode: this.inputs.postalCode.value,
        city: this.inputs.city.value,
        country: this.inputs.country.value,
        countryCode: this.inputs.countryCode.value,
        latitude: this.address.latitude,
        longitude: this.address.longitude
      };
    },
    hasAddressValue() {
      return [...REQUIRED_ADDRESS_FIELDS, 'addressLine2'].some(field => !!this.inputs[field].value);
    },
    isComplete() {
      return REQUIRED_ADDRESS_FIELDS.every(field => !!this.inputs[field].value);
    },
    emitValue() {
      this.$emit('update:value', this.isComplete() ? this.getValue() : '');
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.address-container
  display flex
  flex-direction column
  gap 10px
  padding 12px
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS_LG
  background white

// Le bloc de recherche n'est plus une 2e boîte bordée dans la 1re : simple séparateur
.address-search
  position relative
  padding 10px
  background transparent
  border-bottom 1px solid CTRL_BORDER

.address-search-label
  display flex
  align-items center
  justify-content space-between
  margin-bottom 5px
  font-size 12px
  font-weight 600
  color CTRL_LABEL

.address-collapse
  padding 0 2px
  font inherit
  font-size 12px
  font-weight 700
  color THEME_COLOR_PRIMARY
  background transparent
  border none
  cursor pointer

.address-manual
  margin-top 6px
  padding 2px
  font inherit
  font-size 12.5px
  font-weight 700
  color THEME_COLOR_PRIMARY
  background transparent
  border none
  cursor pointer

.address-summary
  display flex
  align-items center
  gap 12px
  padding 8px 8px 8px 10px
  background rgba(THEME_COLOR_PRIMARY, 0.04)
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS

.address-summary-icon
  display flex
  align-items center
  justify-content center
  flex none
  width 30px
  height 30px
  border-radius 50%
  background rgba(THEME_COLOR_PRIMARY, 0.09)
  color THEME_COLOR_PRIMARY

.address-summary-line
  flex 1
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-size 0.9em
  font-weight 600
  color CTRL_TEXT

.address-summary-edit
  flex none
  padding 6px 10px
  font inherit
  font-size 13px
  font-weight 700
  color THEME_COLOR_PRIMARY
  background white
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS_SM
  cursor pointer
  &:hover
    border-color CTRL_BORDER_HOVER

.address-fields
  display flex
  flex-direction column
  gap 5px
  padding 0 2px

.address-line
  display flex
  gap 10px

.address-line > *
  min-width 0

.street-number
  flex 0 0 100px

.street
  flex 1

.postal-code
  flex 0 0 140px

.city
  flex 1

.country
  flex 1

.country-code
  flex 0 0 125px

.autocomplete-container
  position relative

.suggestions-container
  position absolute
  z-index 1000
  top 100%
  left 0
  width 100%
  margin-top 6px
  overflow hidden
  background white
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  box-shadow 0 12px 28px -10px rgba(23, 35, 63, 0.22), 0 2px 8px rgba(23, 35, 63, 0.08)

.suggestion
  padding 9px 12px
  font-size 13px
  color CTRL_TEXT
  cursor pointer
  transition background-color CTRL_EASE

.suggestion:hover
  background-color rgba(THEME_COLOR_PRIMARY, 0.07)
</style>